// In-memory diagnostic ring buffer for video-call debugging.
//
// The recurring pain on conference issues is that by the time the user
// reports "call dropped at 2:24", DevTools has long been closed and the
// logs that mattered are gone. This module records every signalling /
// reconnect / heartbeat event into a bounded ring buffer that the user
// can dump to clipboard via a button in the conference UI.
//
// It also taps into `console.warn`/`console.error` so any error logged
// elsewhere lands in the same buffer — no need to retrofit every
// component with `diag.log(...)`.
//
// Buffer is bounded (LOG_CAPACITY) so a long call doesn't eat RAM.

const LOG_CAPACITY = 2000;

export interface DiagnosticEntry {
  /** Absolute wall-clock time the entry was recorded, ISO format. */
  ts: string;
  /** Monotonic ms since module load — survives clock skew. */
  rel: number;
  level: "info" | "warn" | "error";
  tag: string;
  message: string;
  detail?: Record<string, unknown>;
}

const buffer: DiagnosticEntry[] = [];
const t0 = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();

function nowRel(): number {
  const t = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
  return Math.round(t - t0);
}

function push(entry: DiagnosticEntry) {
  buffer.push(entry);
  if (buffer.length > LOG_CAPACITY) buffer.splice(0, buffer.length - LOG_CAPACITY);
}

/**
 * Record a structured diagnostic event. Pass a short stable `tag` like
 * `ICE-FLAP` / `PEER-DEAD` / `KEEPALIVE-FAIL` so the dump can be grepped.
 */
export function diag(level: DiagnosticEntry["level"], tag: string, message: string, detail?: Record<string, unknown>): void {
  push({ ts: new Date().toISOString(), rel: nowRel(), level, tag, message, detail });
}

export const diagInfo = (tag: string, msg: string, detail?: Record<string, unknown>) => diag("info", tag, msg, detail);
export const diagWarn = (tag: string, msg: string, detail?: Record<string, unknown>) => diag("warn", tag, msg, detail);
export const diagError = (tag: string, msg: string, detail?: Record<string, unknown>) => diag("error", tag, msg, detail);

/**
 * Install console.warn / console.error taps. Call once at app startup so
 * stack traces from third-party code (mediasoup-client, apollo) land in
 * the dump. Returns a cleanup function (mostly for tests).
 */
let installed = false;
export function installConsoleTaps(): () => void {
  if (installed || typeof console === "undefined") return () => {};
  installed = true;
  const origWarn = console.warn;
  const origError = console.error;
  console.warn = function (...args: unknown[]) {
    try { push({ ts: new Date().toISOString(), rel: nowRel(), level: "warn", tag: "console", message: argsToMessage(args) }); } catch { /* ignore */ }
    origWarn.apply(console, args as []);
  };
  console.error = function (...args: unknown[]) {
    try { push({ ts: new Date().toISOString(), rel: nowRel(), level: "error", tag: "console", message: argsToMessage(args) }); } catch { /* ignore */ }
    origError.apply(console, args as []);
  };
  return () => {
    console.warn = origWarn;
    console.error = origError;
    installed = false;
  };
}

function argsToMessage(args: unknown[]): string {
  return args.map((a) => {
    if (typeof a === "string") return a;
    if (a instanceof Error) return `${a.name}: ${a.message}${a.stack ? "\n" + a.stack : ""}`;
    try { return JSON.stringify(a); } catch { return String(a); }
  }).join(" ");
}

export function getEntries(): DiagnosticEntry[] {
  return buffer.slice();
}

export function clear(): void {
  buffer.length = 0;
}

/**
 * Best-effort `RTCPeerConnection.getStats()` snapshot. We don't have a
 * direct PC reference here, so callers can pass any object that exposes
 * a `getStats()` returning either an `RTCStatsReport` or `any[]`. The
 * return value flattens the report into a plain array safe to JSON.
 */
export async function snapshotRtcStats(getStats: undefined | (() => Promise<unknown>)): Promise<unknown[]> {
  if (typeof getStats !== "function") return [];
  try {
    const report = await getStats();
    if (!report) return [];
    // RTCStatsReport is a Map-like — use forEach.
    if (typeof (report as { forEach?: unknown }).forEach === "function") {
      const out: unknown[] = [];
      (report as { forEach: (cb: (v: unknown) => void) => void }).forEach((v) => out.push(v));
      return out;
    }
    if (Array.isArray(report)) return report;
    return [];
  } catch (e) {
    return [{ error: String((e as Error)?.message ?? e) }];
  }
}

/**
 * Build the full diagnostic dump as text suitable for clipboard paste.
 * Includes platform fingerprint, network state, the log ring, and any
 * RTC stats snapshots the caller provided.
 */
export interface DumpInput {
  /** Optional named getStats() functions for active StreamingHelpers. */
  rtc?: Array<{ name: string; getStats?: () => Promise<unknown> }>;
  /** Any extra context the conference UI wants to include. */
  extra?: Record<string, unknown>;
}

export async function buildDump(input: DumpInput = {}): Promise<string> {
  const env = {
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "(no navigator)",
    platform: typeof navigator !== "undefined" ? (navigator as Navigator & { platform?: string }).platform : undefined,
    language: typeof navigator !== "undefined" ? navigator.language : undefined,
    online: typeof navigator !== "undefined" ? navigator.onLine : undefined,
    cores: typeof navigator !== "undefined" ? (navigator as Navigator & { hardwareConcurrency?: number }).hardwareConcurrency : undefined,
    memory: typeof navigator !== "undefined" ? (navigator as Navigator & { deviceMemory?: number }).deviceMemory : undefined,
    connection: (() => {
      const c = typeof navigator !== "undefined" ? (navigator as Navigator & { connection?: { effectiveType?: string; rtt?: number; downlink?: number } }).connection : undefined;
      if (!c) return undefined;
      return { effectiveType: c.effectiveType, rtt: c.rtt, downlink: c.downlink };
    })(),
    href: typeof window !== "undefined" && window.location ? window.location.href : undefined,
    capturedAt: new Date().toISOString(),
    relMs: nowRel(),
  };

  const rtcSnapshots: Array<{ name: string; stats: unknown[] }> = [];
  if (input.rtc) {
    for (const r of input.rtc) {
      const stats = await snapshotRtcStats(r.getStats);
      rtcSnapshots.push({ name: r.name, stats });
    }
  }

  const payload = {
    env,
    extra: input.extra ?? {},
    rtcSnapshots,
    entries: buffer.slice(),
  };

  return JSON.stringify(payload, null, 2);
}

/**
 * Copy the dump into the clipboard. Returns the dump text on success
 * (useful for tests / fallback display) or rejects on permission denied.
 */
export async function copyDumpToClipboard(input: DumpInput = {}): Promise<string> {
  const text = await buildDump(input);
  if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(text);
  } else {
    // Last-resort: drop into a hidden textarea and select-copy. Some old
    // Safari + Capacitor WebViews still need this.
    if (typeof document !== "undefined") {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } finally { document.body.removeChild(ta); }
    }
  }
  return text;
}
